import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import { Modal } from "../../../../shared/ui/modal";
import type { CenterMember } from "../../../center";
import {
    EMPTY_RATES,
    PAY_RATE_TYPES,
    PayRatesForm,
    hasAnyRate,
    sizesSet,
    useCenterPayRates,
    type PayRates,
} from "../../../reports";

interface Props {
    centerId: string;
    /** The centre's practising professionals, whose rates may be personalised. */
    members: CenterMember[];
}

/**
 * What the centre pays its professionals per session — if it says at all.
 *
 * Optional throughout: a centre that sets nothing keeps its reports as they
 * were, without amounts. A member's override replaces the centre's rate for
 * that person only, type by type.
 */
export default function CenterPayRatesCard({ centerId, members }: Props) {
    const { t, i18n } = useTranslation();
    const { rates, loading, error, retry, saveCentre, saveMember, saving } = useCenterPayRates(centerId);
    const [editing, setEditing] = useState<CenterMember | null>(null);

    const money = (amount: string | null) =>
        amount === null
            ? null
            : new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(
                  Number(amount)
              );
    const summary = (value: PayRates) => {
        const types = PAY_RATE_TYPES.filter((type) => value[type] !== null).map(
            (type) => `${t(`reports.rates.${type}`)}: ${money(value[type])}`
        );
        const sizes = sizesSet(value);
        if (sizes > 0) types.push(t("reports.rates.sizes.configured", { count: sizes }));
        return types.join(" · ");
    };

    const overrideOf = (therapistId: string) =>
        rates?.overrides.find((row) => row.therapist_id === therapistId)?.rates;
    const name = (member: CenterMember) => `${member.first_name} ${member.last_name}`;

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h2 className="text-lg font-semibold text-content-primary">{t("reports.center.title")}</h2>
            <p className="mb-5 mt-1 text-sm text-content-secondary">{t("reports.center.hint")}</p>

            {loading && (
                <p role="status" className="text-sm text-content-secondary">
                    {t("common.loading")}
                </p>
            )}
            {error && (
                <div role="alert" className="flex items-center justify-between gap-3 text-sm text-status-critical">
                    <span>{t("reports.error")}</span>
                    <Button size="sm" variant="outline" onClick={retry}>
                        {t("reports.retry")}
                    </Button>
                </div>
            )}

            {rates && (
                <>
                    <PayRatesForm
                        idPrefix="center-rates"
                        value={rates.center}
                        onSave={saveCentre}
                        saving={saving}
                    />

                    <h3 className="mt-8 text-base font-semibold text-content-primary">
                        {t("reports.center.overridesTitle")}
                    </h3>
                    <p className="mb-3 mt-1 text-sm text-content-secondary">{t("reports.center.overridesHint")}</p>
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                        {members.map((member) => {
                            const override = overrideOf(member.therapist_id);
                            const custom = override && hasAnyRate(override);
                            return (
                                <li
                                    key={member.therapist_id}
                                    className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-content-primary">{name(member)}</p>
                                        <p className="text-xs text-content-secondary">
                                            {custom
                                                ? `${t("reports.center.custom")} — ${summary(override)}`
                                                : t("reports.center.usesCentre")}
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setEditing(member)}>
                                            {t("reports.center.customize")}
                                            <span className="sr-only">: {name(member)}</span>
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}

            {editing && rates && (
                <Modal
                    isOpen
                    onClose={() => setEditing(null)}
                    ariaLabel={t("reports.center.overrideTitle", { name: name(editing) })}
                    className="max-w-2xl p-6"
                >
                    <h2 className="text-lg font-semibold text-content-primary">
                        {t("reports.center.overrideTitle", { name: name(editing) })}
                    </h2>
                    <p className="mb-5 mt-1 text-sm text-content-secondary">{t("reports.center.overridesHint")}</p>
                    <PayRatesForm
                        idPrefix="member-rates"
                        value={overrideOf(editing.therapist_id) ?? EMPTY_RATES}
                        fallback={rates.center}
                        saving={saving}
                        onSave={async (value) => {
                            await saveMember({ therapistId: editing.therapist_id, rates: value });
                            setEditing(null);
                        }}
                        secondaryAction={
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(null)}>
                                {t("reports.center.cancel")}
                            </Button>
                        }
                    />
                </Modal>
            )}
        </section>
    );
}
